/** The menu bar: Radar mark, focused app's name, clock, language, user menu. */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth";
import { appById } from "../apps/registry";
import { launchApp } from "./Desktop";
import { RadarMark } from "./RadarMark";
import { useI18n, ADMIN_LANGUAGE_NAMES, type AdminLocale } from "../lib/i18n";
import { AvatarArt, usePersonalize } from "../lib/personalize";

/** Compact language switcher for the menu bar. */
function LangControl() {
  const { locale, setLocale } = useI18n();
  return (
    <select
      value={locale}
      onChange={(e) => setLocale(e.target.value as AdminLocale)}
      aria-label={ADMIN_LANGUAGE_NAMES[locale]}
      className="cursor-pointer rounded-md bg-transparent px-1.5 py-0.5 text-[13px] text-white/90 outline-none hover:bg-white/10"
      title="Language"
    >
      {(Object.keys(ADMIN_LANGUAGE_NAMES) as AdminLocale[]).map((l) => (
        <option key={l} value={l} className="text-ink">
          {l.toUpperCase()}
        </option>
      ))}
    </select>
  );
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function MenuBar({ focusedApp }: { focusedApp: string }) {
  const { user, signOut } = useAuth();
  const { avatar } = usePersonalize();
  const { t } = useI18n();
  const now = useClock();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [menuOpen]);

  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const date = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <header className="rdr-menubar">
      <span className="grid place-items-center" aria-hidden="true">
        <RadarMark size={16} />
      </span>
      <span className="app-name">{focusedApp}</span>
      <span className="spacer" />
      <span className="clock">
        {date}&ensp;{time}
      </span>
      <LangControl />
      <div ref={menuRef} className="relative">
        <button type="button" onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-1.5">
          {avatar && <AvatarArt id={avatar} size={20} />}
          {user?.displayName || user?.email || t("menu.account")}
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-7 z-[950] min-w-44 rounded-xl p-1.5 text-ink shadow-xl"
            style={{
              background: "color-mix(in srgb, var(--gg-surface-raised) 94%, transparent)",
              backdropFilter: "blur(16px)",
            }}
          >
            <div className="px-3 py-1.5 text-xs text-ink-tertiary">
              {user?.email}
              <span className="ml-1 rounded bg-brand-subtle px-1.5 py-0.5 text-[10px] font-semibold text-brand-on-white uppercase">
                {user?.role}
              </span>
            </div>
            <button
              type="button"
              className="block w-full rounded-lg px-3 py-1.5 text-left text-[13px] font-medium hover:bg-surface-sunken"
              onClick={() => {
                setMenuOpen(false);
                launchApp(appById("profile")!);
              }}
            >
              {t("menu.editProfile")}
            </button>
            <button
              type="button"
              className="block w-full rounded-lg px-3 py-1.5 text-left text-[13px] font-medium hover:bg-surface-sunken"
              onClick={() => void signOut()}
            >
              {t("menu.signOut")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
